/**
 * Appstle Purchase Option Observer for Horizon Theme
 *
 * This module integrates Appstle subscription widgets with the Horizon theme's variant update system.
 * It observes Appstle purchase option selections and price changes, then dispatches events to keep
 * the theme's price display synchronized with the Appstle widget.
 *
 * Features:
 * - Detects when users select one-time or subscription purchase options in Appstle widgets
 * - Monitors price changes when product variants are changed (via variant picker)
 * - Dispatches ThemeEvents.variantUpdate to update the theme's price display
 * - Works with Appstle widgets rendered directly in the DOM (no Shadow DOM)
 * - Scoped to individual product instances using the appstle-product custom element
 *
 * Usage:
 * Wrap product forms and Appstle widgets with <appstle-product> custom element:
 *   <appstle-product>
 *     <product-form-component>...</product-form-component>
 *     <div class="appstle_sub_widget">...</div>
 *   </appstle-product>
 *
 * Events Dispatched:
 * - appstle:selection:change - When a purchase option is selected
 * - ThemeEvents.variantUpdate - When price needs to update (selection or variant change)
 */

(function () {
  'use strict';

  class AppstlePurchaseOptionObserver {
    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
      this.element = element;
      this.initialized = false;
      /** @type {MutationObserver | null} */
      this.selectionObserver = null;
      /** @type {MutationObserver | null} */
      this.widgetContentObserver = null;
      /** @type {MutationObserver | null} */
      this.widgetObserver = null;
      /** @type {HTMLElement | null} */
      this.appstleContainer = null;
      /** @type {HTMLInputElement | null} */
      this.currentSelectedInput = null;
      /** @type {boolean} */
      this.isProcessingPriceUpdate = false;
      /** @type {Set<HTMLInputElement>} */
      this.listenedInputs = new Set();

      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      this.findAppstleContainer();
      this.startObserving();
      this.initialized = true;
    }

    findAppstleContainer() {
      // Look for Appstle widget containers within element
      const appstleSelectors = [
        '.appstle_sub_widget',
        '#appstle_subscription_widget0',
        '[id*="appstle_subscription_widget"]',
        '[id*="appstle-widget"]',
      ];

      for (const selector of appstleSelectors) {
        const widget = this.element.querySelector(selector);
        if (widget) {
          this.appstleContainer = /** @type {HTMLElement} */ (widget);
          return;
        }
      }
    }

    /**
     * @param {HTMLElement} observeTarget
     * @returns {boolean}
     */
    hasSubscriptionOptionsAvailable(observeTarget) {
      // Check if there are subscription-related elements in the widget
      const subscriptionInputs = observeTarget.querySelectorAll(
        'input[type="radio"][name="selling_plan"]:not([value=""])'
      );

      // Also check for subscription text content or labels
      const subscriptionText = observeTarget.textContent || '';
      const hasSubscriptionText =
        /subscription|subscribe|recurring|delivery/i.test(subscriptionText);

      return subscriptionInputs.length > 0 || hasSubscriptionText;
    }

    startObserving() {
      // Start observing the product form area for the Appstle widget to appear
      this.observeForAppstleWidget();
    }

    observeForAppstleWidget() {
      // Look specifically for the appstle widget container within element
      const appstleWidget = this.element.querySelector('.appstle_sub_widget');

      if (appstleWidget) {
        this.checkForExistingWidget();
        return;
      }

      // If not found, observe for the widget to appear
      this.widgetObserver = new MutationObserver((mutations) => {
        this.handleWidgetMutations(mutations);
      });

      // Observe within element for the widget to appear
      this.widgetObserver.observe(this.element, {
        childList: true,
        subtree: true,
      });

      // Also check if widget is already present (fallback)
      this.checkForExistingWidget();
    }

    /**
     * @param {MutationRecord[]} mutations
     */
    handleWidgetMutations(mutations) {
      let foundWidget = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = /** @type {HTMLElement} */ (node);
              // Check specifically for appstle widget
              if (
                element.classList.contains('appstle_sub_widget') ||
                element.id?.includes('appstle_subscription_widget') ||
                element.querySelector('.appstle_sub_widget')
              ) {
                foundWidget = true;
                break;
              }
            }
          }
        }
      }

      if (foundWidget) {
        // Find the widget element
        const widgetElement =
          this.element.querySelector('.appstle_sub_widget') ||
          this.element.querySelector('[id*="appstle_subscription_widget"]');
        if (widgetElement) {
          this.findAppstleContainer();
          if (this.appstleContainer) {
            this.stopWidgetObserver();
            this.setupSelectionObserver();
          }
        }
      }
    }

    checkForExistingWidget() {
      this.findAppstleContainer();
      if (this.appstleContainer) {
        this.stopWidgetObserver();
        this.setupSelectionObserver();
      }
    }

    stopWidgetObserver() {
      if (this.widgetObserver) {
        this.widgetObserver.disconnect();
        this.widgetObserver = null;
      }
    }

    setupSelectionObserver() {
      const observeTarget = this.appstleContainer;

      if (!observeTarget) {
        console.warn(
          '[AppstlePurchaseOptionObserver] Cannot setup selection observer: no container'
        );
        return;
      }

      // Create observer for selection changes and price updates
      this.selectionObserver = new MutationObserver((mutations) => {
        this.handleSelectionMutations(mutations);
      });

      // Observe the entire widget for changes
      // Watch for: checked attribute changes (selection), text content changes (price updates), and child list changes
      this.selectionObserver.observe(observeTarget, {
        attributes: true,
        attributeFilter: ['checked'],
        childList: true,
        subtree: true,
        characterData: true, // Watch for text content changes in price elements
      });

      // Set up observer to detect when widget content is loaded
      // This will also handle widget re-renders when variants change
      this.setupWidgetContentObserver(observeTarget);

      // Also listen for change events on radio inputs (more reliable for user interactions)
      this.setupInputChangeListeners(observeTarget);
    }

    /**
     * @param {HTMLElement} observeTarget
     */
    setupInputChangeListeners(observeTarget) {
      const radioInputs = observeTarget.querySelectorAll(
        'input[type="radio"][name="selling_plan"]'
      );

      radioInputs.forEach((input) => {
        const radioInput = /** @type {HTMLInputElement} */ (input);

        // Only add listener if we haven't already added one to this input
        if (!this.listenedInputs.has(radioInput)) {
          radioInput.addEventListener('change', () => {
            if (radioInput.checked) {
              this.handlePurchaseOptionSelected(radioInput);
            }
          });
          this.listenedInputs.add(radioInput);
        }
      });
    }

    /**
     * @param {HTMLElement} observeTarget
     */
    setupWidgetContentObserver(observeTarget) {
      // First check if content is already loaded
      if (this.isWidgetContentLoaded(observeTarget)) {
        this.checkForInitialSelection();
        // Re-setup input listeners in case widget was re-rendered
        this.setupInputChangeListeners(observeTarget);
      }

      // Disconnect existing observer if any
      if (this.widgetContentObserver) {
        this.widgetContentObserver.disconnect();
      }

      // Set up observer to detect when price elements appear (indicating widget is loaded)
      // Also watch for widget re-renders after variant changes
      // Keep this observer running to catch re-renders and handle cases where widget
      // appears/disappears when variants change
      this.widgetContentObserver = new MutationObserver((mutations) => {
        let hasNewInputs = false;
        let widgetRemoved = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            // Check if widget was removed (variant doesn't have subscription)
            for (const node of mutation.removedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = /** @type {HTMLElement} */ (node);
                if (
                  element.classList.contains('appstle_sub_widget') ||
                  element.id?.includes('appstle_subscription_widget') ||
                  element.querySelector('.appstle_sub_widget')
                ) {
                  widgetRemoved = true;
                  // Clear listeners since widget is gone
                  this.listenedInputs.clear();
                  this.currentSelectedInput = null;
                  break;
                }
              }
            }

            // Check if radio inputs were added (widget re-rendered or appeared)
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = /** @type {HTMLElement} */ (node);
                const isInputElement = element.tagName === 'INPUT';
                const inputElement = isInputElement
                  ? /** @type {HTMLInputElement} */ (element)
                  : null;
                if (
                  element.querySelector(
                    'input[type="radio"][name="selling_plan"]'
                  ) ||
                  (isInputElement &&
                    inputElement &&
                    inputElement.name === 'selling_plan')
                ) {
                  hasNewInputs = true;
                  break;
                }
              }
            }
          }
        }

        // If widget was removed, stop observing and clean up
        if (widgetRemoved) {
          this.stopWidgetObserver();
          return;
        }

        // Re-find widget container in case it was recreated
        if (
          !this.appstleContainer ||
          !this.element.contains(this.appstleContainer)
        ) {
          this.findAppstleContainer();
          if (!this.appstleContainer) {
            // Widget doesn't exist for this variant
            return;
          }
          // Update observeTarget to the new container
          observeTarget = this.appstleContainer;
        }

        // If widget has new inputs and is loaded, re-setup listeners
        if (hasNewInputs && this.isWidgetContentLoaded(observeTarget)) {
          this.listenedInputs.clear(); // Clear old listeners
          this.setupInputChangeListeners(observeTarget);
          // Also check for initial selection in case it changed
          this.checkForInitialSelection();
        } else if (
          this.isWidgetContentLoaded(observeTarget) &&
          !this.initialized
        ) {
          // Initial load complete
          this.checkForInitialSelection();
          this.setupInputChangeListeners(observeTarget);
        }
      });

      // Observe for content changes - keep this running to catch re-renders
      // Observe the element scope to catch widget appearance/disappearance
      this.widgetContentObserver.observe(this.element, {
        childList: true,
        subtree: true,
      });
    }

    /**
     * @param {HTMLElement} observeTarget
     * @returns {boolean}
     */
    isWidgetContentLoaded(observeTarget) {
      // Check if the widget has price elements (indicates it's fully loaded)
      const priceElements = observeTarget.querySelectorAll(
        '.appstle_sellingPlan_price, .appstle_onetime_amount'
      );

      return priceElements.length > 0;
    }

    checkForInitialSelection() {
      const observeTarget = this.appstleContainer;

      if (!observeTarget) {
        return;
      }

      // Check if subscription options are available
      const hasSubscriptionOptions =
        this.hasSubscriptionOptionsAvailable(observeTarget);

      if (!hasSubscriptionOptions) {
        return;
      }

      // Look for currently selected option (checked radio input)
      const selectedInput = /** @type {HTMLInputElement | null} */ (
        observeTarget.querySelector(
          'input[type="radio"][name="selling_plan"]:checked'
        )
      );

      if (selectedInput) {
        // Set current selected input and dispatch event
        this.currentSelectedInput = selectedInput;

        const priceData = this.extractPriceData(selectedInput);

        if (priceData) {
          this.dispatchPurchaseOptionChangeEvent(
            selectedInput,
            priceData,
            'initial-load'
          );
        } else {
          console.warn(
            '[AppstlePurchaseOptionObserver] Failed to extract price data for initial selection'
          );
        }
      }
    }

    /**
     * @param {MutationRecord[]} mutations
     */
    handleSelectionMutations(mutations) {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'checked'
        ) {
          const input = /** @type {HTMLInputElement} */ (mutation.target);
          if (input.checked && input.name === 'selling_plan') {
            this.handlePurchaseOptionSelected(input);
          }
        } else if (
          mutation.type === 'characterData' ||
          mutation.type === 'childList'
        ) {
          // Check if this is a price element that changed
          const target = mutation.target;
          const priceElement =
            target.nodeType === Node.TEXT_NODE
              ? /** @type {HTMLElement} */ (target.parentElement)
              : /** @type {HTMLElement} */ (target);

          if (
            priceElement &&
            (priceElement.classList.contains('appstle_sellingPlan_price') ||
              priceElement.classList.contains('appstle_onetime_amount') ||
              priceElement.classList.contains(
                'appstle_sellingPlan_formattedCompareAtPrice'
              ) ||
              priceElement.classList.contains(
                'appstle_onetime_amount_compare'
              ) ||
              priceElement.querySelector(
                '.appstle_sellingPlan_price, .appstle_onetime_amount'
              ))
          ) {
            // Price content changed, check if we have a selected option
            this.checkForPriceUpdate();
          }
        }
      }
    }

    checkForPriceUpdate() {
      // Prevent infinite loops by checking if we're already processing
      if (this.isProcessingPriceUpdate) {
        return;
      }

      const observeTarget = this.appstleContainer;

      if (!observeTarget) {
        return;
      }

      // Set processing flag to prevent re-entry
      this.isProcessingPriceUpdate = true;

      // Find currently selected option
      const selectedInput = /** @type {HTMLInputElement | null} */ (
        observeTarget.querySelector(
          'input[type="radio"][name="selling_plan"]:checked'
        )
      );

      if (selectedInput && selectedInput === this.currentSelectedInput) {
        // Same option is selected but price might have changed
        const priceData = this.extractPriceData(selectedInput);

        if (priceData) {
          this.dispatchVariantUpdateEvent(selectedInput, priceData);
        }
      } else if (selectedInput) {
        // Different option selected, handle as selection change
        this.handlePurchaseOptionSelected(selectedInput);
      }

      // Reset processing flag
      this.isProcessingPriceUpdate = false;
    }

    /**
     * @param {HTMLInputElement} selectedInput
     */
    handlePurchaseOptionSelected(selectedInput) {
      // Update current selected input
      this.currentSelectedInput = selectedInput;

      const priceData = this.extractPriceData(selectedInput);

      if (priceData) {
        // Dispatch custom event
        this.dispatchPurchaseOptionChangeEvent(
          selectedInput,
          priceData,
          'selection'
        );
      } else {
        console.warn(
          '[AppstlePurchaseOptionObserver] Failed to extract price data'
        );
      }
    }

    /**
     * @param {HTMLInputElement} selectedInput
     * @param {{ price: number; planType: string; hasDiscount: boolean; priceElement: HTMLElement }} priceData
     * @param {string} changeType
     */
    dispatchPurchaseOptionChangeEvent(selectedInput, priceData, changeType) {
      // Dispatch custom event
      const event = new CustomEvent('appstle:selection:change', {
        detail: {
          selectedOption: selectedInput,
          price: priceData.price,
          planType: priceData.planType,
          hasDiscount: priceData.hasDiscount,
          priceElement: priceData.priceElement,
          changeType: changeType, // 'selection' or 'initial-load'
        },
        bubbles: true,
      });

      document.dispatchEvent(event);

      // Also dispatch ThemeEvents.variantUpdate to trigger price updates
      if (priceData.priceElement) {
        this.dispatchVariantUpdateEvent(selectedInput, priceData);
      }
    }

    /**
     * @param {HTMLInputElement} selectedInput
     * @param {{ price: number; planType: string; hasDiscount: boolean; priceElement: HTMLElement }} priceData
     */
    dispatchVariantUpdateEvent(selectedInput, priceData) {
      // Get the product form to find variant and product IDs within element
      const productForm = this.element.querySelector('product-form-component');
      if (!productForm) {
        console.warn(
          '[AppstlePurchaseOptionObserver] Product form not found, cannot dispatch variant update'
        );
        return;
      }

      // Get variant ID from the form
      const variantIdInput = /** @type {HTMLInputElement | null} */ (
        productForm.querySelector('input[name="id"]')
      );
      const variantId = variantIdInput?.value || null;

      // Get product ID from the form component
      const productId =
        /** @type {HTMLElement} */ (productForm).dataset.productId || '';

      // Try to get full variant object from variant-picker's JSON script tag
      // This matches how variant-picker.js gets variant data
      let variantResource = null;
      const variantPicker = this.element.querySelector('variant-picker');
      if (variantPicker) {
        const variantJsonScript = variantPicker.querySelector(
          'script[type="application/json"]'
        );
        if (variantJsonScript?.textContent) {
          try {
            variantResource = JSON.parse(variantJsonScript.textContent);
            // Ensure the variant ID matches what's in the form (in case Appstle changed it)
            if (variantId && variantResource.id !== variantId) {
              variantResource.id = variantId;
            }
          } catch (e) {
            console.warn(
              '[AppstlePurchaseOptionObserver] Failed to parse variant JSON:',
              e
            );
          }
        }
      }

      // Fallback to minimal variant object if we couldn't get full variant data
      if (!variantResource) {
        variantResource = {
          id: variantId || '',
          available: true,
          inventory_management: false,
        };
      } else {
        // Ensure variant ID is set even if JSON didn't have it
        if (!variantResource.id && variantId) {
          variantResource.id = variantId;
        }
        // Ensure required fields are present
        if (variantResource.available === undefined) {
          variantResource.available = true;
        }
        if (variantResource.inventory_management === undefined) {
          variantResource.inventory_management = false;
        }
      }

      // Extract regular price from the Appstle widget
      // We only update the regular price - the theme will handle compare_at_price automatically
      const priceHTML = this.buildPriceHTML(selectedInput, priceData);

      if (!priceHTML) {
        console.warn(
          '[AppstlePurchaseOptionObserver] No price HTML generated, cannot dispatch variant update'
        );
        return;
      }

      // Create synthetic HTML document structure that product-price.js expects
      // Structure: <product-price><div ref="priceContainer">[price HTML]</div></product-price>
      // We only update the regular price - compare_at_price comes from variant data
      const htmlString = `
        <product-price>
          <div ref="priceContainer">${priceHTML}</div>
        </product-price>
      `;

      const html = new DOMParser().parseFromString(htmlString, 'text/html');

      // Dispatch event from product-form-component so event.target.dataset.productId works
      // The event will bubble up to the section where product-price.js listens
      const variantUpdateEvent = new CustomEvent('variant:update', {
        bubbles: true,
        detail: {
          resource: variantResource,
          sourceId: 'appstle-purchase-option',
          data: {
            html,
            productId: productId,
            newProduct: undefined,
          },
        },
      });

      // Dispatch from productForm so event.target is the product-form-component
      productForm.dispatchEvent(variantUpdateEvent);
    }

    /**
     * @param {HTMLInputElement} selectedInput
     * @param {{ price: number; planType: string; hasDiscount: boolean; priceElement: HTMLElement }} priceData
     * @returns {string}
     */
    buildPriceHTML(selectedInput, priceData) {
      // We only update the regular price from Appstle widget
      // The theme will automatically handle compare_at_price from variant data
      const priceElement = priceData.priceElement;
      const priceText = priceElement.textContent?.trim() || '';

      if (!priceText) {
        console.warn(
          '[AppstlePurchaseOptionObserver] No price text found in price element'
        );
        return '';
      }

      // Simply return the regular price - the theme's price component will handle
      // compare_at_price display based on variant data
      return `<span class="price">${priceText}</span>`;
    }

    /**
     * @param {HTMLInputElement} selectedInput
     * @returns {{ price: number; planType: string; hasDiscount: boolean; priceElement: HTMLElement } | null}
     */
    extractPriceData(selectedInput) {
      // Find the wrapper that contains this input
      const wrapper = /** @type {HTMLElement | null} */ (
        selectedInput.closest('.appstle_input_wrapper')
      );

      if (!wrapper) {
        console.warn(
          '[AppstlePurchaseOptionObserver] No wrapper found for selected input'
        );
        return null;
      }

      // Determine if this is a one-time or subscription option
      const isOneTime = this.isOneTimeOption(selectedInput, wrapper);
      const isSubscription = this.isSubscriptionOption(selectedInput, wrapper);

      /** @type {HTMLElement | null} */
      let priceElement = null;
      let planType = 'unknown';
      let hasDiscount = false;

      if (isOneTime) {
        // For one-time purchases, look for onetime_amount
        priceElement = /** @type {HTMLElement | null} */ (
          wrapper.querySelector('.appstle_onetime_amount')
        );
        planType = 'one-time';

        // Check if there's a compare-at price (indicates discount)
        const compareAtPrice = wrapper.querySelector(
          '.appstle_onetime_amount_compare'
        );
        hasDiscount =
          !!compareAtPrice && compareAtPrice.textContent.trim() !== '';
      } else if (isSubscription) {
        // For subscriptions, check for discounted price first, then regular price
        const compareAtPrice = wrapper.querySelector(
          '.appstle_sellingPlan_formattedCompareAtPrice'
        );
        priceElement = /** @type {HTMLElement | null} */ (
          wrapper.querySelector(
            '.appstle_sellingPlan_price:not(.appstle_sellingPlan_formattedCompareAtPrice)'
          )
        );

        planType = 'subscription';
        hasDiscount =
          !!compareAtPrice && compareAtPrice.textContent.trim() !== '';

        // If no price element found, try to find any price element
        if (!priceElement) {
          priceElement = /** @type {HTMLElement | null} */ (
            wrapper.querySelector('.appstle_sellingPlan_price')
          );
        }
      }

      if (!priceElement) {
        console.warn(
          '[AppstlePurchaseOptionObserver] No price element found for selected option'
        );
        return null;
      }

      const price = this.extractPriceFromElement(priceElement);

      if (!price || price <= 0) {
        console.warn(
          '[AppstlePurchaseOptionObserver] Invalid price extracted:',
          price
        );
        return null;
      }

      return {
        price,
        planType,
        hasDiscount,
        priceElement,
      };
    }

    /**
     * @param {HTMLInputElement} input
     * @param {HTMLElement} wrapper
     * @returns {boolean}
     */
    isOneTimeOption(input, wrapper) {
      // Check if input has empty value (one-time purchase)
      if (input.value === '') {
        return true;
      }

      // Check if wrapper has onetime class
      if (wrapper.classList.contains('appstle_onetime_block')) {
        return true;
      }

      // Check for one-time specific elements
      const oneTimeElements = wrapper.querySelectorAll(
        '.appstle_onetime_amount, .appstle_onetime_amount_compare'
      );
      if (oneTimeElements.length > 0) {
        return true;
      }

      // Check label text for one-time indicators
      const label = wrapper.querySelector('label');
      if (label) {
        const labelText = label.textContent || '';
        if (/one-time|one time|single purchase/i.test(labelText)) {
          return true;
        }
      }

      return false;
    }

    /**
     * @param {HTMLInputElement} input
     * @param {HTMLElement} wrapper
     * @returns {boolean}
     */
    isSubscriptionOption(input, wrapper) {
      // If it's not one-time, it's subscription (assuming binary choice)
      if (this.isOneTimeOption(input, wrapper)) {
        return false;
      }

      // Check if input has a selling plan ID value
      if (input.value && input.value !== '') {
        return true;
      }

      // Check for subscription-specific elements
      const subscriptionElements = wrapper.querySelectorAll(
        '.appstle_sellingPlan_price, .appstle_sellingPlan_formattedCompareAtPrice'
      );
      if (subscriptionElements.length > 0) {
        return true;
      }

      // Check label text for subscription indicators
      const label = wrapper.querySelector('label');
      if (label) {
        const labelText = label.textContent || '';
        if (
          /subscription|subscribe|recurring|delivery|monthly|weekly/i.test(
            labelText
          )
        ) {
          return true;
        }
      }

      return false;
    }

    /**
     * @param {HTMLElement} element
     * @returns {number}
     */
    extractPriceFromElement(element) {
      // Get all text content from the element and its children
      const text = element.textContent || element.innerText || '';

      // Try multiple regex patterns to extract price
      const patterns = [
        /\$([0-9,]+\.?\d*)/, // $29.99
        /([0-9,]+\.?\d*)\s*\$/, // 29.99$
        /([0-9,]+\.?\d+)/, // 29.99 (without $)
        /USD\s*([0-9,]+\.?\d*)/i, // USD 29.99
        /([0-9,]+\.?\d*)\s*USD/i, // 29.99 USD
      ];

      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match && match[1]) {
          const price = parseFloat(match[1].replace(/,/g, ''));
          if (price > 0) {
            return price;
          }
        }
      }

      return 0;
    }

    destroy() {
      if (this.selectionObserver) {
        this.selectionObserver.disconnect();
        this.selectionObserver = null;
      }

      if (this.widgetContentObserver) {
        this.widgetContentObserver.disconnect();
        this.widgetContentObserver = null;
      }

      this.stopWidgetObserver();

      this.currentSelectedInput = null;
      this.listenedInputs.clear();
      this.isProcessingPriceUpdate = false;
    }
  }

  // Custom element to scope each Appstle observer instance
  class AppstleProduct extends HTMLElement {
    /** @type {AppstlePurchaseOptionObserver | null} */
    observer = null;

    connectedCallback() {
      this.observer = new AppstlePurchaseOptionObserver(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.destroy();
        this.observer = null;
      }
    }
  }

  if (!customElements.get('appstle-product')) {
    customElements.define('appstle-product', AppstleProduct);
  }
})();
